import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Share2, X, Copy, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackagesPage = () => {
  const { user } = useAuth();
  const isAdmin = !!user && ['admin', 'super_admin'].includes(user.role);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [kuotaModal, setKuotaModal] = useState(false);
  const [typeFilters, setTypeFilters] = useState([{ key: 'all', label: 'Semua' }]);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.package_type = filter;
      const res = await axios.get(`${API}/packages/`, { params });
      setPackages(res.data);
    } catch {
      toast.error('Gagal memuat paket');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  // Fetch active package types for filter + hide packages whose type is inactive
  useEffect(() => {
    axios.get(`${API}/master/package-types`)
      .then(res => {
        const active = res.data.filter(t => t.is_active !== false);
        setTypeFilters([{ key: 'all', label: 'Semua' }, ...active.map(t => ({ key: t.code, label: t.name }))]);
        setPackages(prev => prev.filter(p => active.some(t => t.code === p.package_type)));
      })
      .catch(() => {});
  }, []);

  const handleShare = (pkg) => {
    const url = `${window.location.origin}/packages/${pkg.id}`;
    if (navigator.share) {
      navigator.share({ title: pkg.package_name, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link disalin ke clipboard');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Banner Header */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 py-10 text-center">
        <h1 className="text-3xl font-bold font-['Outfit'] mb-2">Paket Umrah & Haji</h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Pilih paket sesuai kebutuhan Anda
        </p>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Filter */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {typeFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === f.key
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-muted-foreground mt-3">Memuat paket...</p>
          </div>
        )}

        {/* Empty */}
        {!loading && packages.length === 0 && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Belum ada paket tersedia.</p>
          </div>
        )}

        {/* Packages Grid */}
        {!loading && packages.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((pkg) => {
              const hasImage = pkg.images && pkg.images.length > 0;
              const quota = pkg.quota || 0;
              const filled = pkg.kuota_filled || (quota - (pkg.available_quota || 0));
              const percent = quota > 0 ? Math.min((filled / quota) * 100, 100) : 0;
              return (
                <div key={pkg.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
                  {/* Hero Image */}
                  <Link to={`/packages/${pkg.id}`}>
                    <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                      {hasImage ? (
                        <img src={pkg.images[0]} alt={pkg.package_name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 flex items-center justify-center">
                          <span className="text-primary/60 font-bold text-lg">{pkg.package_type.toUpperCase()}</span>
                        </div>
                      )}
                      {pkg.package_type === 'la' && (
                        <span className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">LA</span>
                      )}
                      {pkg.category && (
                        <span className="absolute top-2 right-2 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{pkg.category}</span>
                      )}
                    </div>
                  </Link>

                  {/* Price */}
                  <div className="px-3 pt-3 pb-1">
                    <p className="text-lg font-bold text-[#b8860b]">
                      Rp {pkg.price?.toLocaleString('id-ID')}
                    </p>
                  </div>

                  {/* Title */}
                  <div className="px-3 pb-2 flex-1">
                    <Link to={`/packages/${pkg.id}`} className="text-sm font-medium text-gray-700 hover:text-gray-900 hover:underline leading-snug block">
                      {pkg.package_name}
                    </Link>
                  </div>

                  {/* Quota */}
                  {quota > 0 && (
                    <div className="px-3 pb-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-500">Sisa Kuota</span>
                        <span className="text-xs font-medium text-gray-700">{filled}/{quota} Kursi</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#b8860b] rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="px-3 pb-3 flex items-center gap-2 mt-auto">
                    <Link to={`/packages/${pkg.id}`} className="flex-1">
                      <Button size="sm" className="w-full rounded-full text-xs font-medium bg-[#8B6914] hover:bg-[#7a5c12] text-white shadow-sm transition-colors">
                        Lihat Detail
                      </Button>
                    </Link>
                    <button
                      onClick={() => handleShare(pkg)}
                      className="flex items-center justify-center w-8 h-8 rounded-full border border-[#b8860b] text-[#b8860b] hover:bg-[#b8860b]/10 transition-colors shrink-0"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                    </button>
                    {isAdmin && (
                      <Link
                        to={`/admin?tab=packages&edit=${pkg.id}`}
                        title="Edit paket ini"
                        className="flex items-center justify-center w-8 h-8 rounded-full border border-[#8B6914] text-[#8B6914] hover:bg-[#8B6914]/10 transition-colors shrink-0"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Kuota Modal */}
      {kuotaModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setKuotaModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Cek Kuota Tersedia</h2>
              <button onClick={() => setKuotaModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1">
              <iframe src="https://bisa.work/kuota" className="w-full h-full border-0" title="Kuota" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackagesPage;
